<template>
  <main class="container-1">

    <div class="container-header">
      <Breadcrumb :links="routesBread" />
      <!-- <h2>Capas</h2> -->
      <TabNav :links="routesNav" />
    </div>

    <div class="container-body">

      <!-- Tarjetas resumen -->
      <div class="summary-cards">
        <div class="summary-card">
          <span class="summary-card__value">{{ activeLayers.length }}</span>
          <span class="summary-card__label">Capas activas ahora</span>
        </div>
        <div class="summary-card">
          <span class="summary-card__value">{{ monitoringSummary ? monitoringSummary.sessions_today : '–' }}</span>
          <span class="summary-card__label">Sesiones de capas hoy</span>
        </div>
        <div class="summary-card">
          <span class="summary-card__value">{{ monitoringSummary ? monitoringSummary.active_users : '–' }}</span>
          <span class="summary-card__label">Usuarios activos</span>
        </div>
      </div>

      <hr class="section-divider" />

      <!-- Gráfico: activaciones de capas por fecha -->
      <section class="chart-section">
        <div class="chart-section__header">
          <h3>Activaciones de capas por fecha</h3>
        </div>
        <div class="chart-filters">
          <label class="monitoring-filter-label">Mapa</label>
          <select v-model="c1MapId" class="monitoring-select" @change="loadChart1">
            <option value="">Todos los mapas</option>
            <option v-for="m in mapsList" :key="m.id" :value="m.id">{{ m.name }}</option>
          </select>
          <label class="monitoring-filter-label">Desde</label>
          <input v-model="c1DateFrom" type="date" class="monitoring-filter-date" @change="loadChart1" />
          <label class="monitoring-filter-label">Hasta</label>
          <input v-model="c1DateTo" type="date" class="monitoring-filter-date" @change="loadChart1" />
        </div>
        <apexchart type="line" height="260" :options="c1Options" :series="c1Series" />
      </section>

      <!-- Capas activas ahora -->
      <section class="monitoring-section">
        <div class="monitoring-section__header">
          <h3>Capas activas <span class="monitoring-badge">{{ activeLayers.length }}</span></h3>
          <small class="monitoring-hint">Se actualiza automáticamente cada 30 segundos · última vez: {{ lastRefresh }}</small>
        </div>

        <div class="monitoring-filters">
          <input v-model="filterActive" class="monitoring-filter-input" type="text" placeholder="Buscar por capa, usuario o mapa…" />
          <button v-if="filterActive" class="monitoring-filter-clear" @click="filterActive = ''">✕</button>
        </div>

        <table v-if="activeLayers.length > 0" class="monitoring-table">
          <thead>
            <tr>
              <th>Mapa</th>
              <th>Categoría</th>
              <th>Capa</th>
              <th>Usuario</th>
              <th>Desde</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in activeLayers" :key="item.session_id">
              <td>{{ item.map_name || item.map_id || 'Entrada Vacía' }}</td>
              <td>
                <span v-if="item.is_external_layer" class="badge-external">{{ item.category_name || 'Entrada Vacía' }}</span>
                <span v-else>{{ item.category_name || 'Entrada Vacía' }}</span>
              </td>
              <td>{{ item.layer_name || item.layer_id || 'Entrada Vacía' }}</td>
              <td>{{ [item.firstname, item.lastname].filter(Boolean).join(' ') || 'Entrada Vacía' }}</td>
              <td>{{ item.timestamp_start ? formatDate(item.timestamp_start) : 'Entrada Vacía' }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="monitoring-empty">{{ filterActive ? 'Sin resultados para el filtro aplicado.' : 'No hay capas activas en este momento.' }}</p>
      </section>

      <!-- Últimas sesiones de capas cerradas -->
      <section class="monitoring-section">
        <div class="monitoring-section__header">
          <h3>Últimas sesiones de capas</h3>
        </div>

        <div class="monitoring-filters monitoring-filters--dates">
          <label class="monitoring-filter-label">Desde</label>
          <input v-model="dateFromSessions" type="date" class="monitoring-filter-date" @change="applySessionsFilter" />
          <label class="monitoring-filter-label">Hasta</label>
          <input v-model="dateToSessions" type="date" class="monitoring-filter-date" @change="applySessionsFilter" />
          <button v-if="dateFromSessions || dateToSessions" class="monitoring-filter-clear" @click="clearSessionsDateFilter">✕</button>
        </div>
        <div class="monitoring-filters">
          <input v-model="filterSessions" class="monitoring-filter-input" type="text" placeholder="Buscar por capa, usuario o mapa…" />
          <button v-if="filterSessions" class="monitoring-filter-clear" @click="filterSessions = ''">✕</button>
          <button v-if="hasActiveSessionsFilters" type="button" class="filters-clear-btn" @click="clearSessionsFilters">Limpiar filtros</button>
        </div>

        <table v-if="layerSessions.length > 0" class="monitoring-table">
          <thead>
            <tr>
              <th class="sortable" @click="onSortSessions('map_name')">
                Mapa <font-awesome-icon :icon="getSortIconSessions('map_name')" />
              </th>
              <th class="sortable" @click="onSortSessions('category')">
                Categoría <font-awesome-icon :icon="getSortIconSessions('category')" />
              </th>
              <th class="sortable" @click="onSortSessions('layer_name')">
                Capa <font-awesome-icon :icon="getSortIconSessions('layer_name')" />
              </th>
              <th class="sortable" @click="onSortSessions('user')">
                Usuario <font-awesome-icon :icon="getSortIconSessions('user')" />
              </th>
              <th class="sortable" @click="onSortSessions('timestamp_start')">
                Inicio <font-awesome-icon :icon="getSortIconSessions('timestamp_start')" />
              </th>
              <th class="sortable" @click="onSortSessions('timestamp_end')">
                Término <font-awesome-icon :icon="getSortIconSessions('timestamp_end')" />
              </th>
              <th class="sortable" @click="onSortSessions('duration_seconds')">
                Duración <font-awesome-icon :icon="getSortIconSessions('duration_seconds')" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in layerSessions" :key="item.session_id">
              <td>{{ item.map_name || item.map_id || 'Entrada Vacía' }}</td>
              <td>
                <span v-if="item.is_external_layer" class="badge-external">{{ item.category_name || 'Entrada Vacía' }}</span>
                <span v-else>{{ item.category_name || 'Entrada Vacía' }}</span>
              </td>
              <td>{{ item.layer_name || item.layer_id || 'Entrada Vacía' }}</td>
              <td>{{ [item.firstname, item.lastname].filter(Boolean).join(' ') || 'Entrada Vacía' }}</td>
              <td>{{ item.timestamp_start ? formatDate(item.timestamp_start) : 'Entrada Vacía' }}</td>
              <td>{{ item.timestamp_end ? formatDate(item.timestamp_end) : 'Entrada Vacía' }}</td>
              <td>{{ formatDuration(item.duration_seconds) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="monitoring-empty">No hay sesiones de capas para el filtro aplicado.</p>
        <div v-show="totalPagesLayerSessions > 1" class="pagination-wrap">
          <Pagination
            :totalPages="totalPagesLayerSessions"
            :currentPage="currentPageSessions"
            :pageSize="15"
            @pagechanged="sessionPageChanged"
          />
        </div>
      </section>

    </div>

    <!-- <SideNav class="container-sidenav" :links="routesNav" /> -->

  </main>
</template>

<script>
var moment = require('moment')

import SideNav from '@/components/SideNav.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import Pagination from '@/components/Pagination.vue'
import TabNav from '@/components/TabNav.vue'
import { restMonitoring } from '@/components/mixin/restMonitoring.js'

export default {
  components: { SideNav, Breadcrumb, Pagination, TabNav },
  mixins: [restMonitoring],
  data() {
    return {
      lastRefresh: '--',
      pollingInterval: null,
      filterActive: '',
      filterSessions: '',
      currentPageSessions: 1,
      dateFromSessions: '',
      dateToSessions: '',
      sortKeySessions: '',
      sortOrderSessions: 'asc',
      mapsList: [],
      c1MapId: '',
      c1DateFrom: '',
      c1DateTo: '',
      c1Data: []
    }
  },
  computed: {
    c1Options() {
      return {
        chart: { type: 'line', toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: false }, zoom: { enabled: false } },
        stroke: { curve: 'straight', width: 2 },
        markers: { size: 3, hover: { size: 5 } },
        xaxis: {
          categories: this.c1Data.map(d => d.date), 
          tickAmount: 8, 
          labels: { rotate: -30 } 
        },
        yaxis: { title: { text: 'Activaciones' }, min: 0, forceNiceScale: true },
        colors: ['#3b6e94'],
        dataLabels: { enabled: false },
        tooltip: { theme: 'light' },
        noData: { text: 'Sin datos para el período seleccionado', style: { color: '#aaa', fontSize: '13px' } }
      }
    },
    c1Series() {
      return [{ name: 'Activaciones', data: this.c1Data.map(d => d.count) }]
    },
    hasActiveSessionsFilters() {
      return this.filterSessions !== '' ||
        this.dateFromSessions !== '' ||
        this.dateToSessions !== '' ||
        this.sortKeySessions !== ''
    },
    routesNav() {
      return [
        { name: 'Secciones' },
        { name: 'General', path: '/monitoreo/general' },
        { name: 'Mapas', path: '/monitoreo/mapas' },
        { name: 'Capas', path: '/monitoreo/capas' },
        { name: 'Terreno', path: '/monitoreo/terreno' },
      ]
    },
    routesBread() {
      return [{ name: 'Monitoreo' }]
    }
  },
  watch: {
    filterActive(val) {
      clearTimeout(this._activeSearchTimer)
      this._activeSearchTimer = setTimeout(() => {
        this.getActiveLayers(val)
      }, 400)
    },
    filterSessions(val) {
      clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => {
        this.currentPageSessions = 1
        this.getLayerSessions(1, this.dateFromSessions, this.dateToSessions, val, this.sortKeySessions, this.sortOrderSessions)
      }, 400)
    }
  },
  methods: {
    formatDate(date) {
      moment.locale('es')
      return moment(date).format('DD/MM/YYYY HH:mm:ss')
    },
    relativeTime(date) {
      moment.locale('es')
      return moment(date).fromNow()
    },
    formatDuration(seconds) {
      if (!seconds || seconds < 1) return '< 1 s'
      const s = Math.round(seconds)
      if (s < 60) return `${s} s`
      const m = Math.floor(s / 60)
      const rem = s % 60
      if (m < 60) return rem > 0 ? `${m} min ${rem} s` : `${m} min`
      const h = Math.floor(m / 60)
      const remM = m % 60
      return remM > 0 ? `${h} h ${remM} min` : `${h} h`
    },
    today() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` },
    daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` },
    loadChart1() {
      this.getChartLayerActivity(this.c1MapId, this.c1DateFrom, this.c1DateTo, '')
        .then(data => { this.c1Data = data })
    },
    refreshCapasTab() {
      this.getActiveLayers(this.filterActive).then(() => {
        moment.locale('es')
        this.lastRefresh = moment().format('HH:mm:ss')
      })
    },
    applySessionsFilter() {
      this.currentPageSessions = 1
      this.getLayerSessions(1, this.dateFromSessions, this.dateToSessions, this.filterSessions, this.sortKeySessions, this.sortOrderSessions)
    },
    clearSessionsDateFilter() {
      this.dateFromSessions = ''
      this.dateToSessions = ''
      this.applySessionsFilter()
    },
    clearSessionsFilters() {
      this.filterSessions = ''
      this.dateFromSessions = ''
      this.dateToSessions = ''
      this.sortKeySessions = ''
      this.sortOrderSessions = 'asc'
      this.applySessionsFilter()
      // El watcher de filterSessions agenda un fetch debounced redundante; se cancela
      this.$nextTick(() => clearTimeout(this._searchTimer))
    },
    sessionPageChanged(page) {
      this.currentPageSessions = page
      this.getLayerSessions(page, this.dateFromSessions, this.dateToSessions, this.filterSessions, this.sortKeySessions, this.sortOrderSessions)
    },
    onSortSessions(key) {
      if (this.sortKeySessions === key) {
        if (this.sortOrderSessions === 'asc') {
          this.sortOrderSessions = 'desc'
        } else {
          this.sortKeySessions = ''
          this.sortOrderSessions = 'asc'
        }
      } else {
        this.sortKeySessions = key
        this.sortOrderSessions = 'asc'
      }
      this.currentPageSessions = 1
      this.getLayerSessions(1, this.dateFromSessions, this.dateToSessions, this.filterSessions, this.sortKeySessions, this.sortOrderSessions)
    },
    getSortIconSessions(key) {
      if (this.sortKeySessions !== key) return 'sort'
      return this.sortOrderSessions === 'asc' ? 'sort-up' : 'sort-down'
    }
  },
  created() {
    if (!this.$store.getters.getPermissions || !this.$store.getters.getPermissions.monitoring) {
      this.$router.push('/pagina-no-encontrada')
    }
  },
  mounted() {
    this.dateFromSessions = this.daysAgo(60)
    this.dateToSessions = this.today()
    this.c1DateFrom = this.daysAgo(60)
    this.c1DateTo = this.today()
    this.getMapsList().then(maps => { this.mapsList = maps })
    this.refreshCapasTab()
    this.applySessionsFilter()
    this.loadChart1()
    this.getSummary()
    this.pollingInterval = setInterval(this.refreshCapasTab, 30000)
  },
  beforeDestroy() {
    clearInterval(this.pollingInterval)
    clearTimeout(this._searchTimer)
    clearTimeout(this._activeSearchTimer)
  }
}
</script>

<style lang="scss" scoped>
.summary-cards {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.summary-card {
  flex: 1;
  min-width: 110px;
  padding: 0.85rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;

  &__value {
    display: block;
    font-size: 1.9em;
    font-weight: 700;
    color: var(--color-steel);
    line-height: 1;
  }

  &__label {
    display: block;
    font-size: 0.78em;
    color: #777;
    margin-top: 0.35rem;
  }
}

.monitoring-section {
  margin-bottom: 1rem;

  &__header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
    flex-wrap: wrap;

    h3 {
      margin: 0;
    }
  }
}

.monitoring-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #2b7a78;
  color: #fff;
  border-radius: 999px;
  font-size: 0.85em;
  padding: 0 0.55em;
  min-width: 1.6em;
  height: 1.6em;
  font-weight: 600;
}

.monitoring-hint {
  color: #888;
  font-size: 0.8em;
}

.monitoring-empty {
  color: #888;
  font-style: italic;
}

.monitoring-filters {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;

  &--dates {
    flex-wrap: nowrap;
  }
}

.monitoring-filter-input {
  flex: 1;
  padding: 0.4rem 0.65rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.85em;
  outline: none;

  &:focus {
    border-color: var(--color-steel);
  }
}

.monitoring-filter-label {
  font-size: 0.82em;
  color: #666;
  white-space: nowrap;
}

.monitoring-filter-date {
  padding: 0.3rem 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.85em;
  outline: none;
  min-width: 0;

  &:focus {
    border-color: var(--color-steel);
  }
}

.monitoring-filter-clear {
  background: transparent;
  border: 1px solid #ddd;
  border-radius: 4px;
  color: #888;
  cursor: pointer;
  font-size: 0.8em;
  padding: 0.25rem 0.5rem;
  white-space: nowrap;

  &:hover {
    color: #c0392b;
    border-color: #c0392b;
  }
}

.refresh-btn {
  background: transparent;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  padding: 0.28rem 0.65rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #555;
  font-size: 0.85em;
  line-height: 1;
  white-space: nowrap;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  &:hover {
    border-color: var(--color-steel);
    color: var(--color-steel);
  }
}

.pagination-wrap {
  margin-top: 1rem;
}

.chart-section {
  margin-bottom: 1rem;
  background: #f9fafb;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 1rem 1.25rem 0.75rem;

  &__header h3 {
    margin: 0 0 0.75rem;
    font-size: 1em;
    color: #333;
  }
}

.chart-filters {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  margin-bottom: 0.75rem;
}

.chart-note {
  margin: 0.5rem 0 0;
  font-size: 0.78em;
  color: #f59e0b;
  font-style: italic;
}

.badge-external {
  display: inline-block;
  background: #e8f4fd;
  color: #0a6e9e;
  border: 1px solid #b3d9f5;
  border-radius: 3px;
  font-size: 0.78em;
  font-weight: 600;
  padding: 0.1em 0.45em;
  white-space: nowrap;
}

.monitoring-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9em;

  th, td {
    padding: 0.5rem 0.75rem;
    text-align: left;
    border-bottom: 1px solid #e5e5e5;
  }

  th {
    background: var(--color-navy);
    color: #fff;
    font-weight: 600;
  }

  th.sortable {
    cursor: pointer;
    user-select: none;
  }

  th.sortable:hover {
    background: var(--color-steel);
  }

  tr:hover td {
    background: #fafafa;
  }
}

.section-divider {
  border: none;
  border-top: 1px solid var(--color-line);
  margin: 0 0 1rem;
}
</style>
